/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Resource wrapper container
 *
 * @file ResourceWrapperContainer.jsx
 * @author Srinivasan Bakthavatchalam
 * @since 1.0.1
 */

import PropTypes from 'prop-types';
import { Provider } from 'mobx-react';
import React, { Component } from 'react';

import createStore from '../stores';
import {
  DRAWER_STATE_CLOSED,
  DRAWER_STATE_EXPANDED,
  DRAWER_STATE_OPEN,
  EMPTY_RESOURCE,
  USER_ROLE_INSTRUCTOR
} from '../common/constants';
import Environment from '../config/Environment';
import GlsToken from '../config/GlsToken';
import ResourceContainer from './ResourceContainer';
import ResourceService from '../services/ResourceService';
import StoreRegistry from '../common/StoreRegistry';

import '../assets/styles/style.scss';

class ResourceWrapperContainer extends Component {
  constructor(props) {
    super(props);

    this.initialize();
  }

  componentWillUnmount() {
    (new ResourceService()).end();
  }

  /**
   * Initialize the values
   *
   */
  initialize = () => {
    const {
      currentPageId,
      userRole,
      userSelectedDetails
    } = this.props;

    createStore(userRole, currentPageId, userSelectedDetails);
    this.setEnv();
    this.setGlsToken();
    (new ResourceService()).start();
    this.stores = StoreRegistry.getStores();
  };

  /**
   * Get environment
   *
   */
  setEnv = () => {
    // set the environment globally
    Environment.set(this.props.env);
  };

  /**
   * Set gls token
   *
   */
  setGlsToken = () => {
    // set the gls token globally
    GlsToken.set(this.props.token);
  };

  render() {
    const {
      bookId,
      bulkDownloadStatusCallback,
      cancelCallback,
      currentPageId,
      downloadCallback,
      drawerState,
      isCardView,
      onResourceClick,
      progressCount,
      removeCallback,
      resourceData,
      retryCallback,
      retryCancelCallback,
      selectedResources,
      showDownload,
      userRole
    } = this.props;

    return (
      <div id="vega-resource-wrapper">
        { }
        <Provider {...this.stores}>
          <ResourceContainer
            bookId={bookId}
            bulkDownloadStatusCallback={bulkDownloadStatusCallback}
            cancelCallback={cancelCallback}
            currentPageId={currentPageId}
            downloadCallback={downloadCallback}
            drawerState={drawerState}
            isCardView={isCardView}
            onResourceClick={onResourceClick}
            progressCount={progressCount}
            removeCallback={removeCallback}
            resourceData={resourceData}
            retryCallback={retryCallback}
            retryCancelCallback={retryCancelCallback}
            selectedResources={selectedResources}
            showDownload={showDownload}
            userRole={userRole}
          />
        </Provider>
      </div>
    );
  }
}

ResourceWrapperContainer.propTypes = {
  bookId: PropTypes.string,
  bulkDownloadStatusCallback: PropTypes.func,
  cancelCallback: PropTypes.func,
  currentPageId: PropTypes.array,
  downloadCallback: PropTypes.func,
  drawerState: PropTypes.oneOf([
    DRAWER_STATE_CLOSED,
    DRAWER_STATE_EXPANDED,
    DRAWER_STATE_OPEN
  ]),
  env: PropTypes.string,
  isCardView: PropTypes.bool.isRequired,
  onResourceClick: PropTypes.func,
  progressCount: PropTypes.number,
  removeCallback: PropTypes.func,
  resourceData: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string
  ]),
  retryCallback: PropTypes.func,
  retryCancelCallback: PropTypes.func,
  selectedResources: PropTypes.array,
  showDownload: PropTypes.bool,
  token: PropTypes.string,
  userRole: PropTypes.string,
  userSelectedDetails: PropTypes.object
};

ResourceWrapperContainer.defaultProps = {
  bookId: '',
  bulkDownloadStatusCallback: () => {},
  cancelCallback: () => {},
  currentPageId: [],
  downloadCallback: () => {},
  drawerState: DRAWER_STATE_CLOSED,
  env: 'prod',
  onResourceClick: () => {},
  progressCount: 0,
  removeCallback: () => {},
  resourceData: EMPTY_RESOURCE,
  retryCallback: () => {},
  retryCancelCallback: () => {},
  selectedResources: [],
  showDownload: false,
  token: '',
  userRole: USER_ROLE_INSTRUCTOR,
  userSelectedDetails: {}
};

export default ResourceWrapperContainer;
